/**
 * Notes & files on the new job page (threads/job-page-fresh): the newest notes
 * and messages on the job, + Note, and how many photos and office files there
 * are. Reads the same activity feed and attachment lists as the classic page.
 * Photos and Files jump to the Photos and Paperwork cards on this page.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { classifyTimelineItem, type TimelineItemLike } from "../jobDetailHelpers";
import { ActivityComposer } from "../detail/activity-card";
import type { JobDetail } from "../detail/types";
import { PAPERWORK_ANCHOR, PHOTOS_ANCHOR, scrollToSection, usePaperworkLists } from "./fresh-paperwork";

export function FreshNotes({ job }: { job: JobDetail }) {
    const [composer, setComposer] = useState<"note" | "message" | null>(null);
    const [all, setAll] = useState(false);
    const feedQ = useQuery({
        queryKey: ["activity-timeline", "job", job.id, 50],
        queryFn: () =>
            api.get<{ timeline: TimelineItemLike[] }>(
                `/api/activity?entityType=job&entityId=${encodeURIComponent(job.id)}&limit=50`,
            ),
    });
    // Same query key and URL as the Photos card below, so the number is the
    // number of photos that card shows.
    const photosQ = useQuery({
        queryKey: ["job-photos", job.id],
        queryFn: () =>
            api.get<{ attachments: Array<{ id: string }> }>(`/api/attachments?jobId=${job.id}&category=photo`),
    });
    // Every list the Paperwork card shows (reports, official reports, work
    // orders, uploads, documents), not just plain documents.
    const files = usePaperworkLists(job.id);
    const items = (feedQ.data?.timeline ?? []).filter((i) => {
        const c = classifyTimelineItem(i);
        return all ? c !== "system" : c === "note" || c === "comms";
    });
    const shown = all ? items : items.slice(0, 4);
    return (
        <section
            className="grid content-start gap-2 rounded-2xl border border-border bg-card px-4 py-3 text-card-foreground"
            aria-label="Notes and files"
            data-testid="fresh-notes"
        >
            <h2 className="m-0 text-[19px] font-extrabold">Notes &amp; files</h2>
            <div className="flex flex-wrap gap-2">
                <button
                    type="button"
                    className="min-h-[40px] rounded-lg border border-border px-3 font-semibold hover:bg-muted"
                    onClick={() => setComposer(composer === "note" ? null : "note")}
                >
                    + Note
                </button>
                <button
                    type="button"
                    className="inline-flex min-h-[40px] items-center rounded-lg border border-border px-3 font-semibold hover:bg-muted"
                    onClick={() => scrollToSection(PHOTOS_ANCHOR)}
                    data-testid="fresh-photos-link"
                >
                    {photosQ.data ? `${photosQ.data.attachments.length} photos` : "Photos"}
                </button>
                <button
                    type="button"
                    className="inline-flex min-h-[40px] items-center rounded-lg border border-border px-3 font-semibold hover:bg-muted"
                    onClick={() => scrollToSection(PAPERWORK_ANCHOR)}
                    data-testid="fresh-files-link"
                >
                    {files.loaded ? `${files.total} ${files.total === 1 ? "file" : "files"}` : "Files"}
                </button>
            </div>
            <ActivityComposer job={job} composer={composer} onDone={() => setComposer(null)} />
            {feedQ.isLoading && <p className="m-0 text-muted-foreground">Loading…</p>}
            {!feedQ.isLoading && items.length === 0 && <p className="m-0 text-muted-foreground">No notes yet.</p>}
            <ul className="m-0 grid list-none gap-2 p-0">
                {shown.map((i) => (
                    <li key={i.id} className="border-t border-border pt-2 text-[14.5px]" data-testid="fresh-note">
                        <span className="block text-[13px] text-muted-foreground">
                            {[
                                i.actor?.split(" ")[0],
                                new Date(i.timestamp).toLocaleString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    timeZone: "America/Chicago",
                                }),
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                        <span className="font-semibold">{i.title}</span>
                        {i.detail && <span className="block whitespace-pre-line">{i.detail}</span>}
                    </li>
                ))}
            </ul>
            {items.length > 4 || !all ? (
                <button
                    type="button"
                    className="w-fit text-[14px] font-semibold text-primary underline"
                    onClick={() => setAll((v) => !v)}
                >
                    {all ? "Show only notes" : "Show everything that happened"}
                </button>
            ) : null}
        </section>
    );
}
